import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Typography, Button, Grid, Paper, CircularProgress, Alert, TextField, Select, MenuItem, FormControl, InputLabel, Dialog, DialogTitle, DialogContent, DialogActions, Chip } from '@mui/material';
import { TableChart, Analytics, Add, Edit, Close, Download } from '@mui/icons-material';
import { ExcelLikeEditor } from './ExcelLikeEditor';
import VisualTemplateBuilder from './VisualTemplateBuilder';
import DynamicForm from '../SystemConfiguration/DynamicForm';
import { useApiClient } from '../../config/axios';

interface ViewItem {
  item_id: string;
  parent_item_id?: string | null;
  data_index?: string;
  label_text?: string;
  renderer?: string;
  width?: number | null;
  pinned?: number | null;
  seq_no?: number;
  role?: string;
  extra_props?: Record<string, any>;
  _isNew?: boolean;
  [key: string]: any;
}

// Helper function from SystemConfiguration to normalize rows
function normaliseRow(r: ViewItem) {
  const {
    item_id, parent_item_id,          // keep for tree building
    data_index, label_text, renderer, width, pinned,
    extra_props = {},
    ...dbJunk                         // ← created_at, updated_at, locked, …
  } = r;

  return {
    ...(extra_props || {}),           // <- spread extra_props first
    ...(label_text ? { text: label_text } : {}),
    ...(data_index ? { dataIndex: data_index } : {}),
    ...(renderer ? { renderer } : {}),
    ...(width ? { width } : {}),
    ...(pinned ? { pinned } : {}),
    item_id, parent_item_id
  };
}

interface Template {
  id: string;
  name: string;
  type: string;
  spec: any;
  viewItemCount: number;
  hasMainConfig: boolean;
  hasDefault: boolean;
  keys: string[];
}

interface ComprehensiveCatalog {
  all_keys: Record<string, number>;
  all_paths: string[];
  data_catalog: {
    metrics: string[];
    levels: string[];
    attributes: string[];
    members: string[];
    renderers: string[];
    editors: string[];
    xtypes: string[];
    dimensions: string[];
  };
  key_values: Record<string, string[]>;
  structure_patterns: Record<string, number>;
}


// Enhanced Template List with Search and Edit functionality
function EnhancedTemplateList({
  templates,
  onEditTemplate
}: {
  templates: Template[];
  onEditTemplate: (template: Template) => void;
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      template.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || template.type === typeFilter;
    return matchesSearch && matchesType;
  });

  const allTypes = Array.from(new Set(templates.map(t => t.type))).sort();

  return (
    <Box>
      {/* Search and Filter Controls */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          placeholder="Search templates..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flex: 1 }}
          size="small"
        />
        <FormControl sx={{ minWidth: 150 }} size="small">
          <InputLabel>Type Filter</InputLabel>
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            label="Type Filter"
          >
            <MenuItem value="all">All Types</MenuItem>
            {allTypes.map(type => (
              <MenuItem key={type} value={type}>{type}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="body2" color="textSecondary">
          {filteredTemplates.length} of {templates.length} templates
        </Typography>
      </Box>

      {/* Template Grid */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
        gap: '1.5rem'
      }}>
        {filteredTemplates.map((template) => (
          <Paper
            key={template.id}
            sx={{
              p: 3,
              borderRadius: 2,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              transition: 'transform 0.3s, box-shadow 0.3s',
              overflow: 'hidden',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)'
              }
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="h6"
                  sx={{
                    m: 0,
                    mb: 1,
                    color: '#333',
                    fontSize: '1rem',
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.4
                  }}
                  title={template.name}
                >
                  {template.name}
                </Typography>
                <Chip
                  label={template.type}
                  size="small"
                  sx={{
                    backgroundColor: '#e3f2fd',
                    color: '#1565c0',
                    fontSize: '0.85rem',
                    height: 'auto',
                    py: 0.5,
                    px: 1
                  }}
                />
              </Box>
              <Button
                size="small"
                startIcon={<Edit />}
                onClick={() => onEditTemplate(template)}
                sx={{
                  backgroundColor: '#007bff',
                  color: 'white',
                  fontSize: '0.875rem',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  '&:hover': {
                    backgroundColor: '#0056b3'
                  }
                }}
              >
                Edit
              </Button>
            </Box>

            <Box sx={{
              mb: 2,
              p: 1,
              backgroundColor: '#f8f9fa',
              borderRadius: 1,
              fontSize: '0.9rem'
            }}>
              <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.9rem' }}>
                <strong>View Items:</strong> {template.viewItemCount}
              </Typography>
              {template.hasMainConfig && (
                <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.9rem' }}>
                  <strong>Main Config:</strong> Yes
                </Typography>
              )}
              {template.hasDefault && (
                <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                  <strong>Has Defaults</strong>
                </Typography>
              )}
            </Box>

            <Box sx={{ mt: 2 }}>
              <details>
                <summary style={{
                  cursor: 'pointer',
                  padding: '0.5rem',
                  backgroundColor: '#e9ecef',
                  borderRadius: '4px',
                  marginBottom: '0.5rem',
                  fontSize: '0.875rem'
                }}>
                  View JSON ({JSON.stringify(template.spec).length} chars)
                </summary>
                <pre style={{
                  margin: 0,
                  padding: '1rem',
                  backgroundColor: '#f8f9fa',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                  overflowX: 'auto',
                  maxHeight: '300px',
                  overflowY: 'auto'
                }}>
                  {JSON.stringify(template.spec, null, 2)}
                </pre>
              </details>
            </Box>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}

// Template Editor Modal with Excel/JSON modes
function TemplateEditor({
  template,
  onClose,
  onSave,
  catalog,
  itemCatalog,
  itemCatalogKeys
}: {
  template: Template;
  onClose: () => void;
  onSave: (templateId: string, spec: any) => void;
  catalog: ComprehensiveCatalog | null;
  itemCatalog: Record<string, any>;
  itemCatalogKeys: string[];
}) {
  const [editMode, setEditMode] = useState<'excel' | 'json'>('excel');
  const [templateData, setTemplateData] = useState(template.spec);
  const [saveStatus, setSaveStatus] = useState('');
  const [viewData, setViewData] = useState<ViewItem[]>([]);
  const [rootProps, setRootProps] = useState<Record<string, any>>({});
  const [allowTDOnly, setAllowTDOnly] = useState<boolean>(false);
  const apiClient = useApiClient();

  // Transform template data to DynamicForm format on template change
  useEffect(() => {
    // First, determine which view array to use - could be templateData.view or templateData.main.view
    let viewArray: any[] = [];

    if (templateData?.view && Array.isArray(templateData.view)) {
      viewArray = templateData.view;
    } else if (templateData?.main?.view && Array.isArray(templateData.main.view)) {
      viewArray = templateData.main.view;
    }

    if (viewArray.length > 0) {
      // Convert template view items to ViewItem format with guaranteed unique IDs
      const transformedViewData: ViewItem[] = viewArray.map((item: any, index: number) => {
        // Generate a unique ID if one doesn't exist
        const uniqueId = item.id || item.item_id || `template_item_${Date.now()}_${index}`;

        // Create ViewItem in the same format as SystemConfiguration database rows
        const {
          id, item_id: origItemId, parentId, parent_item_id,
          dataIndex, data_index, text, label, label_text,
          renderer, width, pinned, role, extra_props,
          ...otherProps
        } = item;

        // Create ViewItem in database format - put otherProps into extra_props
        return {
          item_id: uniqueId,
          parent_item_id: parentId || parent_item_id || null,
          data_index: dataIndex || data_index || '',
          label_text: text || label || label_text || '',
          renderer: renderer || '',
          width: width || null,
          pinned: pinned || null,
          role: role || '',
          seq_no: index,
          extra_props: otherProps
        };
      });

      setViewData(transformedViewData);
    } else {
      setViewData([]);
    }

    // Set root props (everything except 'view' and 'main.view')
    const { view, main, ...otherProps } = templateData || {};
    const rootPropsData = { ...otherProps };

    // If main exists, spread its properties except view
    if (main) {
      const { view: mainView, ...mainProps } = main;
      rootPropsData.main = mainProps;
    }

    setRootProps(rootPropsData);
  }, [templateData]);

  // Handle data changes from DynamicForm
  const handleViewDataChange = (newViewData: ViewItem[]) => {
    // Transform back to template format using same approach as normaliseRow but in reverse
    const transformedView = newViewData.map(item => {
      const {
        item_id, parent_item_id, data_index, label_text, seq_no,
        extra_props, width, pinned, renderer, role,
        ...rest
      } = item;

      // Build the transformed item starting with extra_props (spread first)
      const transformedItem: any = {};

      // Spread extra_props first to get all custom properties
      if (extra_props && typeof extra_props === 'object') {
        Object.assign(transformedItem, extra_props);
      }

      // Add any additional properties from rest that aren't ViewItem-specific
      const reservedFields = ['text', 'dataIndex', 'parentId'];
      Object.entries(rest).forEach(([key, value]) => {
        if (!reservedFields.includes(key) && value !== null && value !== '') {
          transformedItem[key] = value;
        }
      });

      // Add core mapped fields if they have values
      if (data_index) transformedItem.dataIndex = data_index;
      if (label_text) transformedItem.text = label_text;
      if (parent_item_id) transformedItem.parentId = parent_item_id;
      if (width !== null && width !== undefined) transformedItem.width = width;
      if (pinned !== null && pinned !== undefined) transformedItem.pinned = pinned;
      if (renderer) transformedItem.renderer = renderer;
      if (role) transformedItem.role = role;

      return transformedItem;
    });

    // Determine where to put the view data - recreate the original structure
    let newTemplateData: any;

    if (templateData?.main?.view !== undefined) {
      // Original structure had main.view
      newTemplateData = {
        ...templateData,
        main: {
          ...templateData.main,
          view: transformedView
        }
      };
    } else {
      // Original structure had direct view
      newTemplateData = {
        ...templateData,
        view: transformedView
      };
    }

    setTemplateData(newTemplateData);
    setViewData(newViewData);
  };

  const handleRootPropsChange = (newRootProps: Record<string, any>) => {
    // Reconstruct template data preserving the original view structure
    let newTemplateData: any;

    if (templateData?.main?.view !== undefined) {
      // Original structure had main.view - restore the main structure
      const { main: newMain, ...otherProps } = newRootProps;
      newTemplateData = {
        ...otherProps,
        main: {
          ...newMain,
          view: templateData.main.view
        }
      };
    } else {
      // Original structure had direct view
      newTemplateData = {
        ...newRootProps,
        view: templateData?.view || []
      };
    }

    setTemplateData(newTemplateData);
    setRootProps(newRootProps);
  };

  const handleSave = async () => {
    try {
      setSaveStatus('💾 Saving...');
      await apiClient.put(`/templates/${template.id}`, {
        spec: templateData,
        name: template.name,
        description: 'Updated template'
      });
      setSaveStatus('✅ Template updated successfully!');
      onSave(template.id, templateData);
      setTimeout(onClose, 1500);
    } catch (err) {
      setSaveStatus('❌ Failed to save template');
      console.error('Save failed:', err);
    }
  };

  // Export view without item_id and parent_item_id
  const handleExportView = () => {
    // Create a clean copy of templateData excluding item_id and parent_item_id
    const cleanData = JSON.parse(JSON.stringify(templateData));

    // Helper function to recursively remove item_id and parent_item_id from objects
    const removeInternalIds = (obj: any): any => {
      if (Array.isArray(obj)) {
        return obj.map(item => removeInternalIds(item));
      } else if (obj && typeof obj === 'object') {
        const { item_id, parent_item_id, ...rest } = obj;
        const cleaned: any = {};
        for (const [key, value] of Object.entries(rest)) {
          cleaned[key] = removeInternalIds(value);
        }
        return cleaned;
      }
      return obj;
    };

    const exportData = removeInternalIds(cleanData);

    // Download as JSON file
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${template.name || 'view'}_template.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      maxWidth={false}
      fullScreen
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          height: '100vh',
          margin: 0,
          borderRadius: 0
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Edit Template: {template.name}</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Mode Toggle */}
          <Box sx={{ display: 'flex', gap: 0.5, bgcolor: 'grey.200', p: 0.5, borderRadius: 1 }}>
            <Button
              size="small"
              variant={editMode === 'excel' ? 'contained' : 'text'}
              onClick={() => setEditMode('excel')}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              📊 Excel
            </Button>
            <Button
              size="small"
              variant={editMode === 'json' ? 'contained' : 'text'}
              onClick={() => setEditMode('json')}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              📄 JSON
            </Button>
          </Box>

          {saveStatus && (
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {saveStatus}
            </Typography>
          )}

          <Button
            variant="outlined"
            startIcon={<Close />}
            onClick={onClose}
            size="small"
          >
            Close
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 0 }}>
        <Box sx={{ flex: 1, overflow: 'auto', p: 2, minHeight: 0 }}>
          {editMode === 'excel' ? (
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <DynamicForm
                viewRows={viewData}
                allowTDOnly={allowTDOnly}
                rootProps={rootProps}
                onDataChange={handleViewDataChange}
                onRootChange={handleRootPropsChange}
                onDirtyChange={() => { }}
                itemCatalog={itemCatalog}
                itemCatalogKeys={itemCatalogKeys}
              />
            </Box>
          ) : (
            <Box sx={{ height: '100%', overflow: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom color="text.secondary">
                📄 View Preview (Read-Only)
              </Typography>
              <Paper sx={{ p: 2, bgcolor: 'grey.50', height: 'calc(100% - 30px)', overflow: 'auto' }}>
                <pre style={{
                  fontSize: '12px',
                  fontFamily: 'Monaco, "Courier New", monospace',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {JSON.stringify(templateData, null, 2)}
                </pre>
              </Paper>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" startIcon={<Close />}>
          Close
        </Button>
        <Button
          onClick={handleExportView}
          variant="outlined"
          startIcon={<Download />}
        >
          Export View
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={editMode === 'excel' && (!templateData?.view || !catalog)}
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}


function ViewTemplateManager() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [catalog, setCatalog] = useState<ComprehensiveCatalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<'list' | 'analyze' | 'create'>('list');
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [itemCatalog, setItemCatalog] = useState<Record<string, any>>({});
  const [itemCatalogKeys, setItemCatalogKeys] = useState<string[]>([]);
  const apiClient = useApiClient();

  const fetchAllTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/templates');
      const templatesWithMetadata = response.data.templates.map((template: any, index: number) => ({
        id: template.name || `template_${index}`,
        name: template.name || `Template ${index + 1}`,
        type: template.type || 'no_type',
        spec: template.spec || {},
        viewItemCount: template.spec?.view?.length || 0,
        hasMainConfig: !!template.spec?.main,
        hasDefault: !!template.spec?.default,
        keys: Object.keys(template.spec || {})
      }));
      setTemplates(templatesWithMetadata);
      setError(null);
    } catch (err) {
      setError('Failed to load templates. Make sure the server is running.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [apiClient]);


  const fetchComprehensiveCatalog = useCallback(async () => {
    try {
      const response = await apiClient.get('/comprehensive-catalog');
      setCatalog(response.data);
    } catch (err) {
      console.log('Comprehensive catalog not available, using basic catalog');
      // Fallback to basic catalog structure
      setCatalog({
        all_keys: {},
        all_paths: [],
        data_catalog: {
          metrics: [],
          levels: [],
          attributes: [],
          members: [],
          renderers: [],
          editors: [],
          xtypes: ['column', 'grid', 'button'],
          dimensions: ['product', 'time', 'geography']
        },
        key_values: {},
        structure_patterns: {}
      });
    }
  }, [apiClient]);

  const fetchItemCatalog = useCallback(async () => {
    try {
      console.log('📚 Fetching item catalog...');
      const response = await apiClient.get('/item-catalog');
      const catalogData = response.data?.catalog || {};
      console.log(`📚 Item catalog loaded with ${Object.keys(catalogData).length} items`);
      setItemCatalog(catalogData);
      setItemCatalogKeys(Object.keys(catalogData));
    } catch (error) {
      console.error('Failed to fetch item catalog:', error);
      setItemCatalog({});
      setItemCatalogKeys([]);
    }
  }, [setItemCatalog, setItemCatalogKeys, apiClient]);

  useEffect(() => {
    Promise.all([
      fetchAllTemplates(),
      fetchComprehensiveCatalog(),
      fetchItemCatalog()
    ]);
  }, [fetchAllTemplates, fetchComprehensiveCatalog, fetchItemCatalog]);

  const handleEditTemplate = (template: Template) => {
    setEditingTemplate(template);
  };

  const handleSaveTemplate = (templateId: string, spec: any) => {
    setTemplates(prev => prev.map(t =>
      t.id === templateId
        ? { ...t, spec, viewItemCount: spec.view?.length || 0 }
        : t
    ));
  };

  const analyzeTemplates = () => {
    const types: Record<string, number> = {};
    templates.forEach(t => {
      const type = t.type || 'no_type';
      types[type] = (types[type] || 0) + 1;
    });

    return (
      <Box>
        <Typography variant="h5" gutterBottom>
          Advanced Template Analysis
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h4" color="primary">
                {templates.length}
              </Typography>
              <Typography variant="subtitle1">
                Total Templates
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h4" color="primary">
                {catalog ? Object.keys(catalog.all_keys).length : 0}
              </Typography>
              <Typography variant="subtitle1">
                Available Keys
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h4" color="primary">
                {catalog ? catalog.all_paths.length : 0}
              </Typography>
              <Typography variant="subtitle1">
                JSON Paths
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h4" color="primary">
                {Object.keys(types).length}
              </Typography>
              <Typography variant="subtitle1">
                Unique Types
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                View Types Distribution
              </Typography>
              <Grid container spacing={2}>
                {Object.entries(types).map(([type, count]) => (
                  <Grid size={{ xs: 6, sm: 4, md: 3 }} key={type}>
                    <Box sx={{ textAlign: 'center', p: 1 }}>
                      <Typography variant="h6" color="primary">{count}</Typography>
                      <Typography variant="body2">{type}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>
          {catalog && (
            <Grid size={{ xs: 12 }}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Data Catalog Summary
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(catalog.data_catalog).map(([category, items]) => (
                    <Grid size={{ xs: 6, sm: 4, md: 3 }} key={category}>
                      <Box sx={{ textAlign: 'center', p: 1 }}>
                        <Typography variant="h6" color="secondary">{items.length}</Typography>
                        <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{category}</Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', gap: 1 }}>
        <Button
          variant={activeView === 'list' ? 'contained' : 'outlined'}
          startIcon={<TableChart />}
          onClick={() => setActiveView('list')}
        >
          Templates & Edit
        </Button>
        <Button
          variant={activeView === 'analyze' ? 'contained' : 'outlined'}
          startIcon={<Analytics />}
          onClick={() => setActiveView('analyze')}
        >
          Analysis
        </Button>
        <Button
          variant={activeView === 'create' ? 'contained' : 'outlined'}
          startIcon={<Add />}
          onClick={() => setActiveView('create')}
        >
          Visual Builder
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading comprehensive template system...</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
          <Button onClick={fetchAllTemplates} sx={{ ml: 2 }}>
            Retry
          </Button>
        </Alert>
      )}

      {!loading && !error && activeView === 'list' && (
        <Box>
          <Typography variant="h5" gutterBottom>
            Templates with Editing ({templates.length} total)
          </Typography>

          <EnhancedTemplateList
            templates={templates}
            onEditTemplate={handleEditTemplate}
          />
        </Box>
      )}

      {!loading && !error && activeView === 'analyze' && analyzeTemplates()}

      {!loading && !error && activeView === 'create' && catalog && (
        <VisualTemplateBuilder
          catalog={catalog}
        />
      )}

      {/* Template Editor Modal */}
      {editingTemplate && !loading && (
        <TemplateEditor
          template={editingTemplate}
          onClose={() => setEditingTemplate(null)}
          onSave={handleSaveTemplate}
          catalog={catalog}
          itemCatalog={itemCatalog}
          itemCatalogKeys={itemCatalogKeys}
        />
      )}
    </Box>
  );
}

export default ViewTemplateManager;